import { type PedestrianDoor, type PedestrianDoorUpsertPayload } from '@m/types/api';
import { apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getLocationDoorsQueryKey } from './query-keys';
import { adminSiteDoorOpen, adminSiteDoors } from './routes';

type OpenDoorVariables = {
  doorId: number;
  locationId: number;
};

type CreateDoorVariables = {
  body: PedestrianDoorUpsertPayload;
  locationId: number;
};

const getDoors = ({ locationId }: { locationId: number }) =>
  apiFetchV2<PedestrianDoor[]>(adminSiteDoors(locationId));

const openDoor = ({ locationId, doorId }: OpenDoorVariables) =>
  apiFetchV2(adminSiteDoorOpen(locationId, doorId), { method: 'POST' });

const createDoor = ({ locationId, body }: CreateDoorVariables) =>
  apiFetchV2(adminSiteDoors(locationId), {
    method: 'PUT',
    body: [body],
  });

export const useLocationDoors = (locationId: number) =>
  useQuery({
    queryKey: getLocationDoorsQueryKey(locationId),
    queryFn: () => getDoors({ locationId }),
    enabled: !!locationId,
  });

export const useOpenDoor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: openDoor,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLocationDoorsQueryKey(variables.locationId),
      });
    },
  });
};

export const useCreateDoor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDoor,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLocationDoorsQueryKey(variables.locationId),
      });
    },
  });
};
